<template>
  <span class="brand-loading-mark" :class="[`brand-loading-mark--${size}`, { 'is-textless': !showText }]" aria-hidden="true">
    <span ref="animationRef" class="brand-loading-mark__icon"></span>
    <span v-if="showText" class="brand-loading-mark__text">{{ text }}</span>
  </span>
</template>

<script setup lang="ts">
  import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import loadingAnimation from '/@/assets/animations/home-data-loading.json';

  type LoadingSize = 'small' | 'large';

  interface LottieAnimation {
    destroy: () => void;
    goToAndPlay?: (value: number, isFrame?: boolean) => void;
  }

  interface LottieData {
    layers?: Array<{
      ks?: {
        o?: {
          k?: number | Array<{ s?: number[] }>;
        };
      };
    }>;
  }

  interface LottiePlayer {
    loadAnimation: (options: { container: HTMLElement; renderer: 'svg'; loop: boolean; autoplay: boolean; animationData: unknown }) => LottieAnimation;
  }

  declare global {
    interface Window {
      lottie?: LottiePlayer;
      __homeLottieLoader?: Promise<LottiePlayer>;
    }
  }

  const props = withDefaults(
    defineProps<{
      text?: string;
      showText?: boolean;
      size?: LoadingSize;
    }>(),
    {
      text: '\u6570\u636e\u6b63\u5728\u52a0\u8f7d\u4e2d',
      showText: true,
      size: 'small',
    }
  );

  const animationRef = ref<HTMLElement | null>(null);
  const SOLID_OPACITY_SCALE = 5;
  let animation: LottieAnimation | undefined;
  let renderVersion = 0;

  onMounted(renderAnimation);
  onBeforeUnmount(() => {
    renderVersion += 1;
    destroyAnimation();
  });

  watch(
    () => props.size,
    () => {
      renderAnimation();
    }
  );

  async function renderAnimation() {
    const container = animationRef.value;
    if (!container) return;

    const currentVersion = ++renderVersion;
    const lottie = await loadLottiePlayer();
    await nextTick();

    if (currentVersion !== renderVersion || animationRef.value !== container) return;

    destroyAnimation();
    container.innerHTML = '';
    animation = lottie.loadAnimation({
      container,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: cloneAnimationData(),
    });
    animation.goToAndPlay?.(props.size === 'large' ? 15 : 0, true);
  }

  function destroyAnimation() {
    animation?.destroy();
    animation = undefined;
  }

  function cloneAnimationData() {
    const animationData = JSON.parse(JSON.stringify(loadingAnimation)) as LottieData;
    if (props.size === 'large') {
      strengthenLayerOpacity(animationData);
    }
    return animationData;
  }

  function strengthenLayerOpacity(animationData: LottieData) {
    animationData.layers?.forEach((layer) => {
      const opacity = layer.ks?.o;
      if (!opacity) return;

      if (typeof opacity.k === 'number') {
        opacity.k = scaleOpacity(opacity.k);
        return;
      }

      opacity.k?.forEach((keyframe) => {
        if (typeof keyframe.s?.[0] === 'number') {
          keyframe.s[0] = scaleOpacity(keyframe.s[0]);
        }
      });
    });
  }

  function scaleOpacity(value: number) {
    return Math.min(100, value * SOLID_OPACITY_SCALE);
  }

  function loadLottiePlayer() {
    if (window.lottie) {
      return Promise.resolve(window.lottie);
    }

    if (window.__homeLottieLoader) {
      return window.__homeLottieLoader;
    }

    window.__homeLottieLoader = new Promise<LottiePlayer>((resolve, reject) => {
      const existingScript = document.querySelector<HTMLScriptElement>('script[data-home-lottie-player]');

      if (existingScript) {
        existingScript.addEventListener('load', () => resolveLoadedPlayer(resolve, reject), { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Failed to load home loading animation player.')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = `${normalizeBaseUrl(import.meta.env.BASE_URL)}animations/lottie-5.9.6.min.js`;
      script.async = true;
      script.dataset.homeLottiePlayer = 'true';
      script.addEventListener('load', () => resolveLoadedPlayer(resolve, reject), { once: true });
      script.addEventListener('error', () => reject(new Error('Failed to load home loading animation player.')), { once: true });
      document.head.appendChild(script);
    });

    return window.__homeLottieLoader;
  }

  function resolveLoadedPlayer(resolve: (player: LottiePlayer) => void, reject: (reason?: unknown) => void) {
    if (window.lottie) {
      resolve(window.lottie);
      return;
    }

    reject(new Error('Home loading animation player is unavailable after script load.'));
  }

  function normalizeBaseUrl(baseUrl: string) {
    return baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  }
</script>

<style scoped lang="less">
  .brand-loading-mark {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    color: #c2c2c2;
    font-size: 10px;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 0;
    text-align: center;

    &__icon {
      flex: 0 0 auto;
      width: 40px;
      height: 30px;
      overflow: hidden;

      :deep(svg) {
        display: block;
        width: 100% !important;
        height: 100% !important;
      }
    }

    &__text {
      display: block;
      min-width: 70px;
      max-width: 136px;
      color: #c2c2c2;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &.is-textless {
      gap: 0;
    }

    &--large {
      width: 160px;
      height: 120px;

      .brand-loading-mark__icon {
        width: 132px;
        height: 99px;
      }
    }
  }
</style>
